import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';

interface ScrolledProps {
  scrolled: boolean;
}

const HeaderContainer = styled.header<ScrolledProps>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  transition: background-color 0.3s ease, box-shadow 0.3s ease;
  padding: 1rem 0;
  background-color: ${({ theme, scrolled }) => 
    scrolled ? theme.colors.background : 'transparent'};
  box-shadow: ${({ theme, scrolled }) => 
    scrolled ? theme.shadows.small : 'none'};
`;

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const Logo = styled(Link)<ScrolledProps>`
  font-family: ${({ theme }) => theme.fonts.accent};
  font-size: 1.8rem;
  color: ${({ theme, scrolled }) => 
    scrolled ? theme.colors.primary : theme.colors.background};
  letter-spacing: 1px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.5rem;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

const NavLink = styled(Link)<ScrolledProps>`
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  position: relative;
  color: ${({ theme, scrolled }) => 
    scrolled ? theme.colors.text : theme.colors.background};
  
  &:after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 0;
    height: 1px;
    background-color: ${({ theme }) => theme.colors.primary};
    transition: width 0.3s ease;
  }
  
  &:hover:after {
    width: 100%;
  }
`;

const MobileMenuButton = styled.button<ScrolledProps>`
  display: none;
  font-size: 1.5rem;
  color: ${({ theme, scrolled }) => 
    scrolled ? theme.colors.primary : theme.colors.background};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: block;
  }
`;

const MobileMenu = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 70%;
  max-width: 300px;
  height: 100vh;
  background-color: ${({ theme }) => theme.colors.background};
  box-shadow: ${({ theme }) => theme.shadows.large};
  transform: translateX(${({ isOpen }) => (isOpen ? '0' : '100%')});
  transition: transform 0.3s ease;
  z-index: 1001;
  padding: 5rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const MobileNavLink = styled(Link)`
  font-size: 1.2rem;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: ${({ theme }) => theme.colors.text};
  padding: 0.5rem 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.secondary};
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.primary};
`;

const Overlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

const Header: React.FC = () => {
  const { pathname } = useLocation();
  const isHome = pathname === '/';
  const [scrolledPastHero, setScrolledPastHero] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const scrolled = !isHome || scrolledPastHero;

  useEffect(() => {
    if (!isHome) return;
    const handleScroll = () => {
      setScrolledPastHero(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHome]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // Create a custom component that doesn't pass scrolled to the DOM element
  const StyledMobileMenuButton: React.FC<{ onClick: () => void; scrolled: boolean; children: React.ReactNode }> = ({ 
    onClick, scrolled, children 
  }) => (
    <MobileMenuButton onClick={onClick} scrolled={scrolled}>
      {children}
    </MobileMenuButton>
  );

  return (
    <>
      <HeaderContainer scrolled={scrolled}>
        <Nav>
          <Logo to="/" scrolled={scrolled}>
            Ajana Designs
          </Logo>
          <NavLinks>
            <NavLink to="/" scrolled={scrolled}>
              Home
            </NavLink>
            <NavLink to="/collections" scrolled={scrolled}>
              Collections
            </NavLink>
            {/* <NavLink to="/about" scrolled={scrolled}>
              About
            </NavLink>
            <NavLink to="/contact" scrolled={scrolled}>
              Contact
            </NavLink> */}
          </NavLinks>
          <StyledMobileMenuButton onClick={toggleMobileMenu} scrolled={scrolled}>
            ☰
          </StyledMobileMenuButton>
        </Nav>
      </HeaderContainer>

      <MobileMenu isOpen={mobileMenuOpen}>
        <CloseButton onClick={closeMobileMenu}>✕</CloseButton>
        <MobileNavLink to="/" onClick={closeMobileMenu}>
          Home
        </MobileNavLink>
        <MobileNavLink to="/collections" onClick={closeMobileMenu}>
          Collections
        </MobileNavLink>
        <MobileNavLink to="/about" onClick={closeMobileMenu}>
          About
        </MobileNavLink>
        <MobileNavLink to="/contact" onClick={closeMobileMenu}>
          Contact
        </MobileNavLink>
      </MobileMenu>
      <Overlay isOpen={mobileMenuOpen} onClick={closeMobileMenu} />
    </>
  );
};

export default Header;
